import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api/client';
import LoadingSpinner from '../components/LoadingSpinner';
import DiaryManagerStats from '../components/DiaryManagerStats';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Handler {
  handler_id: number;
  handler_name: string;
  email_address: string;
  user_job: string;
  title: string;
  open_claims: number;
  total_incurred: number;
  avg_claim_age_days: number;
  overdue_diaries: number;
  job_label: string;
}

interface HandlerWorkloadResponse {
  handlers: Handler[];
  count: number;
  total_open_claims: number;
  total_incurred: number;
}

type SortKey = 'handler_name' | 'job_label' | 'open_claims' | 'avg_claim_age_days' | 'overdue_diaries' | 'total_incurred';
type SortDir = 'asc' | 'desc';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatCompactCurrency(value: number | null | undefined): string {
  if (value == null) return '—';
  if (Math.abs(value) >= 1_000_000) {
    return `$${(value / 1_000_000).toFixed(1)}M`;
  }
  if (Math.abs(value) >= 1_000) {
    return `$${(value / 1_000).toFixed(0)}K`;
  }
  return `$${value.toFixed(0)}`;
}

function truncateName(name: string, maxLen: number): string {
  if (name.length <= maxLen) return name;
  return name.slice(0, maxLen - 1) + '…';
}

// ─── Custom Tooltip ───────────────────────────────────────────────────────────

interface ChartTooltipProps {
  active?: boolean;
  payload?: Array<{ value: number; name: string }>;
  label?: string;
  formatter?: (value: number) => string;
}

function ChartTooltip({ active, payload, label, formatter }: ChartTooltipProps) {
  if (!active || !payload || payload.length === 0) return null;
  const val = payload[0].value;
  return (
    <div className="bg-white border border-slate-200 shadow-md rounded-lg px-3 py-2 text-sm">
      <p className="font-semibold text-slate-700 mb-1">{label}</p>
      <p className="text-slate-600">{formatter ? formatter(val) : val.toLocaleString()}</p>
    </div>
  );
}

// ─── Stat Card ────────────────────────────────────────────────────────────────

interface StatCardProps {
  label: string;
  value: string | number;
  sub?: string;
}

function StatCard({ label, value, sub }: StatCardProps) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-6 py-5 flex flex-col gap-1 min-w-0">
      <p className="text-sm font-medium text-slate-500 truncate">{label}</p>
      <p className="text-3xl font-bold text-blue-700 tabular-nums truncate">{value}</p>
      {sub && <p className="text-xs text-slate-400 truncate">{sub}</p>}
    </div>
  );
}

// ─── Sort Header ──────────────────────────────────────────────────────────────

interface SortHeaderProps {
  label: string;
  colKey: SortKey;
  sortKey: SortKey;
  sortDir: SortDir;
  onSort: (key: SortKey) => void;
  align?: 'left' | 'right';
}

function SortHeader({ label, colKey, sortKey, sortDir, onSort, align = 'left' }: SortHeaderProps) {
  const active = sortKey === colKey;
  return (
    <th
      className={`px-4 py-3 font-semibold text-slate-600 whitespace-nowrap cursor-pointer select-none hover:text-blue-700 transition-colors ${align === 'right' ? 'text-right' : 'text-left'}`}
      onClick={() => onSort(colKey)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <span className="text-slate-400 text-xs">
          {active ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}
        </span>
      </span>
    </th>
  );
}

// ─── HandlersView ─────────────────────────────────────────────────────────────

export default function HandlersView() {
  const [data, setData] = useState<HandlerWorkloadResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [sortKey, setSortKey] = useState<SortKey>('open_claims');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result: HandlerWorkloadResponse = await api.getHandlerWorkload();
      setData(result);
    } catch (err: any) {
      setError(err?.message || 'Failed to load handler workload.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ── Sort handlers ───────────────────────────────────────────────────────────

  function handleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  }

  const handlers = data?.handlers ?? [];

  const sortedHandlers = [...handlers].sort((a, b) => {
    const aVal = a[sortKey];
    const bVal = b[sortKey];
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return sortDir === 'asc'
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    }
    const aNum = aVal as number;
    const bNum = bVal as number;
    return sortDir === 'asc' ? aNum - bNum : bNum - aNum;
  });

  // ── Chart data ──────────────────────────────────────────────────────────────

  const topByOpenClaims = [...handlers]
    .sort((a, b) => b.open_claims - a.open_claims)
    .slice(0, 10)
    .map((h) => ({
      name: truncateName(h.handler_name, 15),
      open_claims: h.open_claims,
    }));

  const topByAvgAge = [...handlers]
    .sort((a, b) => b.avg_claim_age_days - a.avg_claim_age_days)
    .slice(0, 10)
    .map((h) => ({
      name: truncateName(h.handler_name, 15),
      avg_claim_age_days: h.avg_claim_age_days,
    }));

  const handlersWithOverdue = [...handlers]
    .filter((h) => h.overdue_diaries > 0)
    .sort((a, b) => b.overdue_diaries - a.overdue_diaries)
    .map((h) => ({
      name: truncateName(h.handler_name, 15),
      overdue_diaries: h.overdue_diaries,
    }));

  // ── Overdue color helper ────────────────────────────────────────────────────

  function overdueCellClass(count: number): string {
    if (count > 5) return 'font-semibold text-red-600';
    if (count > 0) return 'font-medium text-amber-600';
    return 'text-green-600';
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">

        {/* Page title */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Handler Workload</h1>
          <p className="text-sm text-slate-500 mt-0.5">Team Performance Dashboard</p>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-24">
            <LoadingSpinner message="Loading handler workload…" />
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div className="bg-white rounded-xl border border-red-200 shadow-sm px-6 py-10 flex flex-col items-center gap-4">
            <svg className="w-10 h-10 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
              />
            </svg>
            <p className="text-slate-700 font-medium">{error}</p>
            <button
              onClick={fetchData}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
            >
              Retry
            </button>
          </div>
        )}

        {/* Content */}
        {!loading && !error && data && (
          <>
            {/* ── Summary Stats ─────────────────────────────────────────── */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <StatCard
                label="Total Handlers"
                value={data.count.toLocaleString()}
                sub="active claim handlers"
              />
              <StatCard
                label="Total Open Claims"
                value={data.total_open_claims.toLocaleString()}
                sub="across all handlers"
              />
              <StatCard
                label="Total Incurred"
                value={formatCompactCurrency(data.total_incurred)}
                sub="aggregate portfolio exposure"
              />
            </div>

            {/* ── Charts ────────────────────────────────────────────────── */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

              {/* Chart 1: Open Claims */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                <p className="text-base font-semibold text-slate-700 mb-4">Open Claims by Handler</p>
                {topByOpenClaims.length === 0 ? (
                  <p className="text-slate-400 text-sm text-center py-8">No data</p>
                ) : (
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart
                      data={topByOpenClaims}
                      layout="vertical"
                      margin={{ top: 0, right: 16, left: 0, bottom: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
                      <XAxis
                        type="number"
                        tick={{ fontSize: 11, fill: '#64748b' }}
                        tickLine={false}
                        axisLine={false}
                      />
                      <YAxis
                        type="category"
                        dataKey="name"
                        width={100}
                        tick={{ fontSize: 11, fill: '#64748b' }}
                        tickLine={false}
                        axisLine={false}
                      />
                      <Tooltip
                        content={
                          <ChartTooltip formatter={(v) => `${v.toLocaleString()} claims`} />
                        }
                        cursor={{ fill: '#eff6ff' }}
                      />
                      <Bar dataKey="open_claims" fill="#3b82f6" radius={[0, 3, 3, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>

              {/* Chart 2: Avg Claim Age */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                <p className="text-base font-semibold text-slate-700 mb-4">Avg Claim Age by Handler</p>
                {topByAvgAge.length === 0 ? (
                  <p className="text-slate-400 text-sm text-center py-8">No data</p>
                ) : (
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart
                      data={topByAvgAge}
                      layout="vertical"
                      margin={{ top: 0, right: 16, left: 0, bottom: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
                      <XAxis
                        type="number"
                        tick={{ fontSize: 11, fill: '#64748b' }}
                        tickLine={false}
                        axisLine={false}
                      />
                      <YAxis
                        type="category"
                        dataKey="name"
                        width={100}
                        tick={{ fontSize: 11, fill: '#64748b' }}
                        tickLine={false}
                        axisLine={false}
                      />
                      <Tooltip
                        content={
                          <ChartTooltip formatter={(v) => `${v.toLocaleString()} days`} />
                        }
                        cursor={{ fill: '#fffbeb' }}
                      />
                      <Bar dataKey="avg_claim_age_days" fill="#f59e0b" radius={[0, 3, 3, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>

              {/* Chart 3: Overdue Diaries */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                <p className="text-base font-semibold text-slate-700 mb-4">Overdue Diaries by Handler</p>
                {handlersWithOverdue.length === 0 ? (
                  <p className="text-sm text-center text-green-600 font-medium py-8">
                    No overdue diaries — all caught up.
                  </p>
                ) : (
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart
                      data={handlersWithOverdue}
                      layout="vertical"
                      margin={{ top: 0, right: 16, left: 0, bottom: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
                      <XAxis
                        type="number"
                        tick={{ fontSize: 11, fill: '#64748b' }}
                        tickLine={false}
                        axisLine={false}
                        allowDecimals={false}
                      />
                      <YAxis
                        type="category"
                        dataKey="name"
                        width={100}
                        tick={{ fontSize: 11, fill: '#64748b' }}
                        tickLine={false}
                        axisLine={false}
                      />
                      <Tooltip
                        content={
                          <ChartTooltip formatter={(v) => `${v.toLocaleString()} overdue`} />
                        }
                        cursor={{ fill: '#fef2f2' }}
                      />
                      <Bar dataKey="overdue_diaries" fill="#ef4444" radius={[0, 3, 3, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            {/* ── Diary Manager Stats ──────────────────────────────────── */}
            <DiaryManagerStats />

            {/* ── Data Table ────────────────────────────────────────────── */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <SortHeader
                        label="Handler Name"
                        colKey="handler_name"
                        sortKey={sortKey}
                        sortDir={sortDir}
                        onSort={handleSort}
                      />
                      <SortHeader
                        label="Role"
                        colKey="job_label"
                        sortKey={sortKey}
                        sortDir={sortDir}
                        onSort={handleSort}
                      />
                      <SortHeader
                        label="Open Claims"
                        colKey="open_claims"
                        sortKey={sortKey}
                        sortDir={sortDir}
                        onSort={handleSort}
                        align="right"
                      />
                      <SortHeader
                        label="Avg Age (days)"
                        colKey="avg_claim_age_days"
                        sortKey={sortKey}
                        sortDir={sortDir}
                        onSort={handleSort}
                        align="right"
                      />
                      <SortHeader
                        label="Overdue Diaries"
                        colKey="overdue_diaries"
                        sortKey={sortKey}
                        sortDir={sortDir}
                        onSort={handleSort}
                        align="right"
                      />
                      <SortHeader
                        label="Total Incurred"
                        colKey="total_incurred"
                        sortKey={sortKey}
                        sortDir={sortDir}
                        onSort={handleSort}
                        align="right"
                      />
                    </tr>
                  </thead>
                  <tbody>
                    {sortedHandlers.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-4 py-12 text-center text-slate-400">
                          No handler data available.
                        </td>
                      </tr>
                    ) : (
                      sortedHandlers.map((handler) => (
                        <tr
                          key={handler.handler_id}
                          className="border-b border-slate-100 hover:bg-blue-50 transition-colors"
                        >
                          <td className="px-4 py-3 text-slate-800 font-medium whitespace-nowrap">
                            {handler.handler_name || '—'}
                          </td>
                          <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                            {handler.job_label || handler.title || '—'}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-slate-800 whitespace-nowrap">
                            {handler.open_claims.toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-slate-700 whitespace-nowrap">
                            {handler.avg_claim_age_days != null
                              ? Math.round(handler.avg_claim_age_days).toLocaleString()
                              : '—'}
                          </td>
                          <td className={`px-4 py-3 text-right tabular-nums whitespace-nowrap ${overdueCellClass(handler.overdue_diaries)}`}>
                            {handler.overdue_diaries.toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-slate-800 font-medium whitespace-nowrap">
                            {formatCompactCurrency(handler.total_incurred)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Table footer */}
              {sortedHandlers.length > 0 && (
                <div className="px-4 py-3 border-t border-slate-100 bg-slate-50 text-xs text-slate-400">
                  {sortedHandlers.length.toLocaleString()} handler{sortedHandlers.length !== 1 ? 's' : ''}
                  {' '}&mdash; click column headers to sort
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
